import { getFormFieldsWithInitialValues } from '../getFormFieldsWithInitialValues';

/**
 * Marks what a submitter must not change: the values already in the form
 * (previous submitters' answers, the initiator's entries) unless the form lets
 * previous submissions be edited. Mutates and returns `element`.
 */
export const markSubmitterReadOnly = (element, formData) => {
  const readOnly = formData.can_edit_previous_submissions ? null : 'readonly';

  if (element.type === 'name' || element.type === 'address' || element.type === 'checkbox') {
    element.inputs.map((input) => {
      if (input.valueInput) {
        input['readOnly'] = readOnly;
      }

      return input;
    });
  }

  if (element.type === 'table') {
    element.rows.map((row) => {
      if (row.cols) {
        row.cols.forEach((col) => {
          if (col?.value) {
            col['readOnly'] = readOnly;
          }
        });
      }
      return row;
    });
  }

  if (element.options.valueInput) {
    element.options['readOnly'] = readOnly;
  }

  return element;
};

/**
 * The fields a submitter starts filling in from, built from a `get-form` answer:
 * the initiation's fields, overlaid with what was already submitted (kept at the
 * field's designed position, read-only unless previous submissions may be
 * edited) and with the submitter's own saved draft. A form nobody has touched
 * yet gets its auto-set dates/times for `userFormRole`.
 *
 * Returns a fresh copy; `formData` is left alone so it can be rebuilt from
 * (e.g. "Clear form").
 *
 * @param {object} formData - The `get-form` response.
 * @param {string|undefined} userFormRole - See `useUserFormRole`.
 * @returns {Array|undefined}
 */
export const buildSubmitterFormFields = (formData, userFormRole) => {
  const data = JSON.parse(JSON.stringify(formData ?? {}));

  if (!data.form_fields) {
    return undefined;
  }

  if (data.submitted_data?.length) {
    return data.form_fields.map((form_field) => {
      const submitted_item = data.submitted_data.find((item) => form_field.id === item.id);
      const saved_item = data.saved_data?.find((item) => form_field.id === item.id);

      if (submitted_item) {
        submitted_item.options.top = form_field.options.top;
        return markSubmitterReadOnly(submitted_item, data);
      }

      if (saved_item) {
        return saved_item;
      }

      return markSubmitterReadOnly(form_field, data);
    });
  }

  if (data.saved_data?.length) {
    return data.form_fields.map((form_field) => {
      const saved_item = data.saved_data.find((item) => form_field.id === item.id);

      if (saved_item) {
        return saved_item;
      }

      return markSubmitterReadOnly(form_field, data);
    });
  }

  return getFormFieldsWithInitialValues(
    data.form_fields.map((item) => markSubmitterReadOnly(item, data)),
    userFormRole,
  );
};
